import { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';

import { useInboxContext } from 'providers/InboxProvider';
import { UserAvatar } from 'shared/components/symbols';
import { SchoolMemberFragment, UserInfoFragment } from 'shared/graphql';
import { useResponsive } from 'shared/hooks';
import { generateUserProfileRoute } from 'shared/routes';

import { Container, Content, Description } from './ExplorerUserCard.styles';
import { ExplorerUserCardButtons } from './ExplorerUserCardButtons';
import { ExplorerUserCardHeader } from './ExplorerUserCardHeader';

interface Props {
  data: UserInfoFragment | SchoolMemberFragment;

  /**
   * Whether to show a divider at the end of the card.
   * @default false
   */
  withDivider?: boolean;

  /** @default false */
  withMessage?: boolean;
  /** @default false */
  withViewProfile?: boolean;
  /** Allow to extend the current button options. */
  addons?: ReactNode;
}

export function ExplorerUserCard(props: Props) {
  const description = props.data?.professionalDesignations;

  const withMessage = props.withMessage ?? false;
  const withViewProfile = props.withViewProfile ?? false;

  const { push: navigate } = useHistory();
  const { isMobile } = useResponsive();
  const { onOpenInboxModal } = useInboxContext();

  const openProfile = () => {
    if (!props.data?.id) {
      return;
    }

    const url = generateUserProfileRoute(props.data.id);
    navigate(url);
  };

  const openMessage = () => {
    if (!onOpenInboxModal) {
      return;
    }

    onOpenInboxModal({
      isOpen: true,
      options: {
        members: [props.data],
        messageType: 'personal',
      },
    });
  };

  return (
    <Container borderless={isMobile} withDivider={props.withDivider}>
      <UserAvatar user={props.data} />

      <Content>
        <ExplorerUserCardHeader user={props.data} />

        {description && <Description>{description}</Description>}
      </Content>

      <ExplorerUserCardButtons
        withMessage={withMessage}
        withViewProfile={withViewProfile}
        addons={props.addons}
        onOpenProfile={openProfile}
        onOpenMessage={openMessage}
      />
    </Container>
  );
}
